/**
 * Sprite animation system — TypeScript port of dd-ascii-anim sprite.rs + sprites_builtin.rs.
 *
 * Provides frame-based ASCII sprite animation with a clip library and playback
 * controller. Space characters within sprite frames are treated as transparent
 * and do not overwrite existing buffer contents.
 *
 * Modules ported:
 *   sprite.rs          -> SpriteFrame, AnimationClip, SpriteAnimator
 *   sprites_builtin.rs -> warrior/mage/npc factory functions
 */

import { Color, FrameBuffer } from "./engine";

// ---------------------------------------------------------------------------
// SpriteFrame
// ---------------------------------------------------------------------------

/**
 * A single animation frame as lines of ASCII text.
 *
 * Each line is stored as a string. The frame tracks the maximum width
 * across all lines and the total height (line count).
 */
export class SpriteFrame {
  private readonly lines: string[];
  private readonly _width: number;
  private readonly _height: number;

  /**
   * Create a new sprite frame from string lines.
   *
   * Width is computed as the longest line; height is the number of lines.
   * An empty array produces a zero-dimension frame.
   */
  constructor(lines: string[]) {
    this.lines = [...lines];
    this._width = lines.reduce((max, line) => Math.max(max, line.length), 0);
    this._height = lines.length;
  }

  /** Get the frame width (widest line, in characters). */
  width(): number {
    return this._width;
  }

  /** Get the frame height (number of lines). */
  height(): number {
    return this._height;
  }

  /**
   * Render the frame into a FrameBuffer at position (x, y).
   *
   * `x` is the column offset and `y` is the row offset. Space characters
   * are treated as transparent and do not overwrite buffer contents.
   * Characters that fall outside the buffer bounds are silently clipped.
   */
  render(buffer: FrameBuffer, x: number, y: number, fg: Color, bg: Color): void {
    for (let rowIdx = 0; rowIdx < this.lines.length; rowIdx++) {
      const line = this.lines[rowIdx];
      const bufRow = y + rowIdx;
      for (let colIdx = 0; colIdx < line.length; colIdx++) {
        const ch = line[colIdx];
        if (ch === " ") {
          continue; // transparent
        }
        const bufCol = x + colIdx;
        buffer.set(bufRow, bufCol, ch, fg, bg);
      }
    }
  }
}

// ---------------------------------------------------------------------------
// AnimationClip
// ---------------------------------------------------------------------------

/**
 * A sequence of frames forming an animation clip.
 *
 * Each clip has a name, a list of SpriteFrames, a duration per frame
 * (in ticks), and a looping flag. Non-looping clips stop on the last frame.
 */
export class AnimationClip {
  private readonly _name: string;
  private readonly _frames: SpriteFrame[];
  private readonly _frameDuration: number;
  private readonly _looping: boolean;

  /**
   * Create a new animation clip.
   *
   * @param name - Identifier for the clip (e.g. "idle", "attack").
   * @param frames - Ordered list of SpriteFrames.
   * @param frameDuration - Ticks per frame (clamped to >= 1).
   * @param looping - Whether to restart from frame 0 after the last frame.
   */
  constructor(name: string, frames: SpriteFrame[], frameDuration: number, looping: boolean) {
    this._name = name;
    this._frames = frames;
    this._frameDuration = Math.max(1, frameDuration);
    this._looping = looping;
  }

  /** Get the number of frames in this clip. */
  frameCount(): number {
    return this._frames.length;
  }

  /** Get the clip name. */
  name(): string {
    return this._name;
  }

  /** Get a specific frame by index, or undefined if out of bounds. */
  frame(index: number): SpriteFrame | undefined {
    return this._frames[index];
  }

  /** Whether this clip loops. */
  isLooping(): boolean {
    return this._looping;
  }

  /** Ticks per frame. */
  frameDuration(): number {
    return this._frameDuration;
  }
}

// ---------------------------------------------------------------------------
// SpriteAnimator
// ---------------------------------------------------------------------------

/**
 * Manages sprite state and current animation playback.
 *
 * The animator holds a collection of named AnimationClips and tracks which
 * clip is playing, the current frame index, and tick accumulation. Call
 * update() each tick to advance the animation, then render() to blit the
 * current frame onto a buffer.
 */
export class SpriteAnimator {
  private clips: Map<string, AnimationClip> = new Map();
  private currentClip: string | null = null;
  private _currentFrame = 0;
  private ticksOnFrame = 0;
  private _position: [number, number] = [0, 0];
  private _playing = false;
  private _fg: Color = Color.white();
  private _bg: Color = Color.black();

  /** Add an animation clip to the library. Replaces any existing clip with the same name. */
  addClip(clip: AnimationClip): void {
    this.clips.set(clip.name(), clip);
  }

  /**
   * Start playing the named clip, resetting the frame counter.
   *
   * If the clip name is not found in the library this is a no-op and
   * the animator remains in its previous state.
   */
  play(clipName: string): void {
    if (this.clips.has(clipName)) {
      this.currentClip = clipName;
      this._currentFrame = 0;
      this.ticksOnFrame = 0;
      this._playing = true;
    }
  }

  /**
   * Advance the animation by one tick.
   *
   * If no clip is playing or the animator is stopped this is a no-op.
   * When accumulated ticks reach the clip's frameDuration the frame
   * advances. On looping clips the frame wraps to 0; on non-looping clips
   * the animator stops after the last frame has been displayed for its full
   * duration.
   */
  update(): void {
    if (!this._playing) return;
    if (this.currentClip === null) return;

    const clip = this.clips.get(this.currentClip);
    if (!clip) return;
    if (clip.frameCount() === 0) return;

    this.ticksOnFrame += 1;

    if (this.ticksOnFrame >= clip.frameDuration()) {
      this.ticksOnFrame = 0;
      const next = this._currentFrame + 1;

      if (next >= clip.frameCount()) {
        if (clip.isLooping()) {
          this._currentFrame = 0;
        } else {
          // Stay on last frame, stop playing
          this._playing = false;
        }
      } else {
        this._currentFrame = next;
      }
    }
  }

  /**
   * Render the current frame onto the given FrameBuffer.
   *
   * Uses the animator's position and colors. If no clip is active or the
   * clip has no frames this is a no-op.
   */
  render(buffer: FrameBuffer): void {
    if (this.currentClip === null) return;

    const clip = this.clips.get(this.currentClip);
    if (!clip) return;

    const frame = clip.frame(this._currentFrame);
    if (frame) {
      const [x, y] = this._position;
      frame.render(buffer, x, y, this._fg, this._bg);
    }
  }

  /** Set the sprite position on the buffer as (x_col, y_row). */
  setPosition(x: number, y: number): void {
    this._position = [x, y];
  }

  /** Get the current position as [x_col, y_row]. */
  position(): [number, number] {
    return this._position;
  }

  /** Whether the animator is currently playing a clip. */
  isPlaying(): boolean {
    return this._playing;
  }

  /** Get the name of the currently active clip, if any. */
  currentClipName(): string | null {
    return this.currentClip;
  }

  /** Get the current frame index within the active clip. */
  currentFrameIndex(): number {
    return this._currentFrame;
  }

  /** Set the foreground color used when rendering sprites. */
  setFg(fg: Color): void {
    this._fg = fg;
  }

  /** Set the background color used when rendering sprites. */
  setBg(bg: Color): void {
    this._bg = bg;
  }
}

// ---------------------------------------------------------------------------
// Built-in sprites (from sprites_builtin.rs)
// ---------------------------------------------------------------------------
// All sprites use pure alphanumeric and symbol glyphs only -- no box-drawing
// characters. Sprites are 5 characters wide and 3-4 lines tall.

// ---- Warrior ---------------------------------------------------------------

/** Warrior idle stance (single frame, looping). */
export function warriorIdle(): AnimationClip {
  return new AnimationClip("warrior_idle", [new SpriteFrame([" O  ", "/|\\ ", "/ \\ "])], 8, true);
}

/** Warrior walk cycle (4 frames, looping). Alternates leg positions. */
export function warriorWalk(): AnimationClip {
  return new AnimationClip(
    "warrior_walk",
    [
      new SpriteFrame([" O  ", "/|\\ ", "/ \\ "]),
      new SpriteFrame([" O  ", "/|\\ ", "/|  "]),
      new SpriteFrame([" O  ", "/|\\ ", " |\\ "]),
      new SpriteFrame([" O  ", "/|\\ ", "/ \\ "]),
    ],
    4,
    true,
  );
}

/** Warrior attack animation (3 frames, not looping). Sword swing from raised to extended. */
export function warriorAttack(): AnimationClip {
  return new AnimationClip(
    "warrior_attack",
    [
      new SpriteFrame([" O/ ", "/|  ", "/ \\ "]),
      new SpriteFrame([" O  ", "/|\\ ", "/ \\ "]),
      new SpriteFrame([" O_ ", " |/ ", "/ \\ "]),
    ],
    3,
    false,
  );
}

/** Warrior hurt reaction (2 frames, not looping). Recoil from taking damage. */
export function warriorHurt(): AnimationClip {
  return new AnimationClip(
    "warrior_hurt",
    [new SpriteFrame(["  O ", " /| ", " / \\"]), new SpriteFrame([" O  ", "/|\\ ", "/ \\ "])],
    4,
    false,
  );
}

// ---- Mage ------------------------------------------------------------------

/** Mage idle stance (single frame, looping). Robed figure with staff. */
export function mageIdle(): AnimationClip {
  return new AnimationClip(
    "mage_idle",
    [new SpriteFrame([" O  ", "/|\\ ", "|_| ", "/ \\ "])],
    8,
    true,
  );
}

/** Mage spellcasting animation (4 frames, not looping). Hands raise and energy gathers. */
export function mageCast(): AnimationClip {
  return new AnimationClip(
    "mage_cast",
    [
      new SpriteFrame([" O  ", "/|\\ ", "|_| ", "/ \\ "]),
      new SpriteFrame([" O  ", "\\|/ ", "|_| ", "/ \\ "]),
      new SpriteFrame(["*O* ", "\\|/ ", "|_| ", "/ \\ "]),
      new SpriteFrame(["~O~ ", "\\|/ ", "|_| ", "/ \\ "]),
    ],
    3,
    false,
  );
}

// ---- NPC -------------------------------------------------------------------

/** Generic NPC idle stance (single frame, looping). Lowercase 'o' head to distinguish from player. */
export function npcIdle(): AnimationClip {
  return new AnimationClip("npc_idle", [new SpriteFrame([" o  ", "/|\\ ", "/ \\ "])], 8, true);
}

/** All built-in sprite clip names for enumeration. */
export const BUILTIN_CLIP_NAMES = [
  "warrior_idle",
  "warrior_walk",
  "warrior_attack",
  "warrior_hurt",
  "mage_idle",
  "mage_cast",
  "npc_idle",
] as const;

export type BuiltinClipName = (typeof BUILTIN_CLIP_NAMES)[number];

/** Factory map for creating built-in clips by name. */
const BUILTIN_FACTORIES: Record<BuiltinClipName, () => AnimationClip> = {
  warrior_idle: warriorIdle,
  warrior_walk: warriorWalk,
  warrior_attack: warriorAttack,
  warrior_hurt: warriorHurt,
  mage_idle: mageIdle,
  mage_cast: mageCast,
  npc_idle: npcIdle,
};

/**
 * Create a SpriteAnimator pre-loaded with all built-in clips.
 *
 * Useful for quick prototyping — call animator.play("warrior_idle") etc.
 */
export function createBuiltinAnimator(): SpriteAnimator {
  const animator = new SpriteAnimator();
  for (const factory of Object.values(BUILTIN_FACTORIES)) {
    animator.addClip(factory());
  }
  return animator;
}
